import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { 
  Upload, FileSpreadsheet, FileJson, Download, AlertCircle, 
  CheckCircle, Trash2, Clock, ArrowLeft, Eye, PlayCircle
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../../components/Header';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BulkImportPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('csv');
  const [file, setFile] = useState(null);
  const [jsonData, setJsonData] = useState('');
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importHistory, setImportHistory] = useState([]);
  
  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchHistory();
  }, [user, navigate]);
  
  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/admin/import/history`, { withCredentials: true });
      setImportHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };
  
  const downloadTemplate = async (format) => {
    try {
      const response = await axios.get(`${API}/admin/import/template/${format}`, { withCredentials: true });
      
      if (format === 'csv') {
        const blob = new Blob([response.data.template_csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'materials_template.csv';
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(response.data.template, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'materials_template.json';
        a.click();
        URL.revokeObjectURL(url);
      }
      
      toast.success(`${format.toUpperCase()} template downloaded`);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(null);
    }
  };
  
  const previewImport = async () => {
    if (!file && activeTab === 'csv') {
      toast.error('Please select a file');
      return;
    }
    
    setImporting(true);
    try {
      if (activeTab === 'csv') {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/admin/import/preview/csv`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setPreview(response.data);
      } else {
        // JSON preview
        const parsed = JSON.parse(jsonData);
        const response = await axios.post(`${API}/admin/import/json`, {
          ...parsed,
          dry_run: true
        }, { withCredentials: true });
        
        setPreview(response.data);
      }
      
      toast.success('Preview generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview failed');
    } finally {
      setImporting(false);
    }
  };
  
  const executeImport = async () => {
    if (!preview) {
      toast.error('Please preview first');
      return;
    }
    
    setImporting(true);
    try {
      let response;
      
      if (activeTab === 'csv') {
        const formData = new FormData();
        formData.append('file', file);
        
        response = await axios.post(`${API}/admin/import/execute/csv`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        const parsed = JSON.parse(jsonData);
        response = await axios.post(`${API}/admin/import/json`, {
          ...parsed,
          dry_run: false
        }, { withCredentials: true });
      }
      
      toast.success(`${response.data.imported} materials imported!`);
      setFile(null);
      setJsonData('');
      setPreview(null);
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };
  
  const rollbackBatch = async (batchId) => {
    if (!window.confirm('Are you sure? This will delete all materials from this import batch.')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/admin/import/batch/${batchId}`, { withCredentials: true });
      toast.success(`${response.data.materials_deleted} materials deleted`);
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rollback failed');
    }
  };
  
  return (
    <div className="min-h-screen bg-neutral-50" data-testid="bulk-import-page">
      <Header />
      
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-neutral-500 hover:text-neutral-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </button>
          <h1 className="text-3xl font-light">Bulk Material Import</h1>
          <p className="text-neutral-500 mt-2">Import multiple materials via CSV or JSON</p>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => { setActiveTab('csv'); setPreview(null); }}
            className={`flex items-center gap-2 px-4 py-2 border ${
              activeTab === 'csv' ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 hover:bg-neutral-50'
            }`}
          >
            <FileSpreadsheet className="w-4 h-4" />
            CSV Import
          </button>
          <button
            onClick={() => { setActiveTab('json'); setPreview(null); }}
            className={`flex items-center gap-2 px-4 py-2 border ${
              activeTab === 'json' ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 hover:bg-neutral-50'
            }`}
          >
            <FileJson className="w-4 h-4" />
            JSON Import
          </button>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Import Form */}
          <div className="bg-white border border-neutral-200 p-6">
            <h2 className="text-lg font-medium mb-4">
              {activeTab === 'csv' ? 'Upload CSV File' : 'Paste JSON Data'}
            </h2>
            
            {/* Download Template */}
            <button
              onClick={() => downloadTemplate(activeTab)}
              className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 mb-6"
            >
              <Download className="w-4 h-4" />
              Download {activeTab.toUpperCase()} Template
            </button>
            
            {activeTab === 'csv' ? (
              <div>
                <label className="block">
                  <div className={`border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
                    file ? 'border-green-400 bg-green-50' : 'border-neutral-300 hover:border-neutral-500'
                  }`}>
                    <Upload className={`w-10 h-10 mx-auto mb-3 ${file ? 'text-green-600' : 'text-neutral-400'}`} />
                    {file ? (
                      <div>
                        <p className="font-medium text-green-700">{file.name}</p>
                        <p className="text-sm text-green-600">Click to change file</p>
                      </div>
                    ) : (
                      <div>
                        <p className="font-medium">Click to upload CSV</p>
                        <p className="text-sm text-neutral-500">or drag and drop</p>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="csv-file-input"
                  />
                </label>
              </div>
            ) : (
              <div>
                <textarea
                  value={jsonData}
                  onChange={(e) => setJsonData(e.target.value)}
                  placeholder='{"materials": [{"name": "...", "stone_type": "..."}], "dry_run": true}'
                  rows={12}
                  className="w-full border border-neutral-300 p-3 font-mono text-sm"
                  data-testid="json-input"
                />
              </div>
            )}
            
            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={previewImport}
                disabled={importing || (activeTab === 'csv' && !file) || (activeTab === 'json' && !jsonData)}
                className="flex items-center gap-2 px-4 py-2 border border-neutral-300 hover:bg-neutral-50 disabled:opacity-50"
                data-testid="preview-btn"
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
              
              {preview && (
                <button
                  onClick={executeImport}
                  disabled={importing || preview.errors?.length > 0}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                  data-testid="execute-btn"
                >
                  <PlayCircle className="w-4 h-4" />
                  {importing ? 'Importing...' : 'Execute Import'}
                </button>
              )}
            </div>
          </div>
          
          {/* Preview Results */}
          <div className="bg-white border border-neutral-200 p-6">
            <h2 className="text-lg font-medium mb-4">Preview Results</h2>
            
            {preview ? (
              <div>
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-neutral-50 p-4 text-center">
                    <p className="text-2xl font-medium">{preview.total_rows || preview.total_materials || 0}</p>
                    <p className="text-sm text-neutral-500">Total Rows</p>
                  </div>
                  <div className="bg-green-50 p-4 text-center">
                    <p className="text-2xl font-medium text-green-700">{preview.valid_materials || preview.total_materials || 0}</p>
                    <p className="text-sm text-green-600">Valid</p>
                  </div>
                  <div className="bg-red-50 p-4 text-center">
                    <p className="text-2xl font-medium text-red-700">{preview.errors?.length || 0}</p>
                    <p className="text-sm text-red-600">Errors</p>
                  </div>
                </div>
                
                {/* Errors */}
                {preview.errors && preview.errors.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-medium text-red-600 mb-2 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" /> Errors
                    </h3>
                    <div className="bg-red-50 border border-red-200 p-3 max-h-40 overflow-y-auto">
                      {preview.errors.map((err, i) => (
                        <p key={i} className="text-sm text-red-700">
                          Row {err.row}: {err.error}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Preview Items */}
                <h3 className="font-medium mb-2">Preview (first 5)</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {(preview.preview || preview.all_materials || []).slice(0, 5).map((mat, i) => (
                    <div key={i} className="bg-neutral-50 p-3 text-sm">
                      <p className="font-medium">{mat.name}</p>
                      <p className="text-neutral-500">
                        {mat.stone_type} • {mat.color || 'N/A'} • {mat.origin || 'N/A'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-neutral-400">
                <Eye className="w-12 h-12 mx-auto mb-3" />
                <p>Preview will appear here</p>
              </div>
            )}
          </div>
        </div>
        
        {/* Import History */}
        <div className="mt-12">
          <h2 className="text-xl font-light mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" /> Import History
          </h2>
          
          {importHistory.length > 0 ? (
            <div className="bg-white border border-neutral-200">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-neutral-50">
                    <th className="p-4 text-left text-sm font-medium">Batch ID</th>
                    <th className="p-4 text-left text-sm font-medium">Date</th>
                    <th className="p-4 text-left text-sm font-medium">Imported</th>
                    <th className="p-4 text-left text-sm font-medium">Failed</th>
                    <th className="p-4 text-left text-sm font-medium">Status</th>
                    <th className="p-4 text-left text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {importHistory.map((log) => (
                    <tr key={log.batch_id} className="border-b last:border-0">
                      <td className="p-4 text-sm font-mono">{log.batch_id}</td>
                      <td className="p-4 text-sm">{new Date(log.created_at).toLocaleString()}</td>
                      <td className="p-4 text-sm text-green-600">{log.successful}</td>
                      <td className="p-4 text-sm text-red-600">{log.failed}</td>
                      <td className="p-4 text-sm">
                        {log.rolled_back ? (
                          <span className="bg-red-100 text-red-700 px-2 py-1 text-xs">Rolled Back</span>
                        ) : (
                          <span className="bg-green-100 text-green-700 px-2 py-1 text-xs">Active</span>
                        )}
                      </td>
                      <td className="p-4">
                        {!log.rolled_back && (
                          <button
                            onClick={() => rollbackBatch(log.batch_id)}
                            className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" /> Rollback
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white border border-neutral-200 p-8 text-center text-neutral-400">
              No import history yet
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
